import React, { useState, useEffect } from 'react';
import { 
  IndianRupee, Calculator, Users, Search, Save, 
  FileDown, Check, Clock, Calendar, Upload, User, Building2, ChevronDown, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { api } from '../lib/api';
import { toast } from 'sonner';
import PINGate from '../components/PINGate';

const HRSalary = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [calculatedSalary, setCalculatedSalary] = useState(null);
  const [pinVerified, setPinVerified] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [salaryTypes, setSalaryTypes] = useState([]);
  const [expandedDepts, setExpandedDepts] = useState({});
  const [viewMode, setViewMode] = useState('department'); // 'department' or 'list'

  // Salary config form - supports all three types
  const [salaryConfig, setSalaryConfig] = useState({
    salary_type: 'hourly_teachers',
    // Hourly Teachers fields
    fix_hourly_rate: 0,
    // Daily Teachers/Non-Teaching fields
    fix_gross: 0,
    min_fix_hours_commitment: 0,
    fix_pf: 0,
    fix_esic: 0,
    // Common fields
    bonus: 0,
    penalty: 0,
    increment: 0,
    tds: 0,
    arrear: 0,
    gwr: 0,
    consistency_bonus: 0,
    remarks: ''
  });

  // Calculate form
  const [calcForm, setCalcForm] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    month_working_hours: 0,
    monthly_attendance: 26,
    total_working_days: 26
  });

  useEffect(() => {
    if (pinVerified) {
      fetchEmployees();
      fetchSalaryTypes();
      fetchDepartments();
    }
  }, [pinVerified]);

  const fetchEmployees = async () => {
    try {
      const response = await api.getHREmployees();
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.getDepartments();
      setDepartments(response.data);
      // Expand all departments
      const expanded = {};
      response.data.forEach(d => expanded[d.name] = true);
      expanded['Unassigned'] = true;
      setExpandedDepts(expanded);
    } catch (error) {
      console.error('Failed to load departments');
    }
  };

  const fetchSalaryTypes = async () => {
    try {
      const response = await api.getSalaryTypes();
      setSalaryTypes(response.data.salary_types || []);
    } catch (error) {
      console.error('Failed to load salary types');
    }
  };

  const openConfigModal = async (employee) => {
    setSelectedEmployee(employee);
    try {
      const response = await api.getSalaryConfig(employee.id);
      if (response.data) {
        setSalaryConfig({
          salary_type: response.data.salary_type || 'hourly_teachers',
          fix_hourly_rate: response.data.fix_hourly_rate || 0,
          fix_gross: response.data.fix_gross || 0,
          min_fix_hours_commitment: response.data.min_fix_hours_commitment || 0,
          fix_pf: response.data.fix_pf || 0,
          fix_esic: response.data.fix_esic || 0,
          bonus: response.data.bonus || 0,
          penalty: response.data.penalty || 0,
          increment: response.data.increment || 0,
          tds: response.data.tds || 0,
          arrear: response.data.arrear || 0,
          gwr: response.data.gwr || 0,
          consistency_bonus: response.data.consistency_bonus || 0,
          remarks: response.data.remarks || ''
        });
      } else {
        // Reset to defaults
        setSalaryConfig({
          salary_type: 'hourly_teachers',
          fix_hourly_rate: 0,
          fix_gross: 0,
          min_fix_hours_commitment: 0,
          fix_pf: 0,
          fix_esic: 0,
          bonus: 0,
          penalty: 0,
          increment: 0,
          tds: 0,
          arrear: 0,
          gwr: 0,
          consistency_bonus: 0,
          remarks: ''
        });
      }
      setShowConfigModal(true);
    } catch (error) {
      toast.error('Failed to load salary config');
    }
  };

  const saveSalaryConfig = async () => {
    // Validate based on salary type
    if (salaryConfig.salary_type === 'hourly_teachers' && salaryConfig.fix_hourly_rate <= 0) {
      toast.error('Fix Hourly Rate must be greater than 0');
      return;
    }
    if (salaryConfig.salary_type !== 'hourly_teachers' && salaryConfig.fix_gross <= 0) {
      toast.error('Fix Gross must be greater than 0');
      return;
    }

    setSaving(true);
    try {
      await api.setSalaryConfig({
        employee_id: selectedEmployee.id,
        ...salaryConfig
      });
      toast.success('Salary configuration saved');
      setShowConfigModal(false);
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to save salary config');
    } finally {
      setSaving(false);
    }
  };

  const openCalcModal = (employee) => {
    setSelectedEmployee(employee);
    setCalcForm({
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      month_working_hours: 0,
      monthly_attendance: 26,
      total_working_days: 26
    });
    setCalculatedSalary(null);
    setShowCalcModal(true);
  };

  const calculateSalary = async () => {
    try {
      const response = await api.calculateSalary({
        employee_id: selectedEmployee.id,
        month: calcForm.month,
        year: calcForm.year,
        month_working_hours: calcForm.month_working_hours,
        monthly_attendance: calcForm.monthly_attendance,
        total_working_days: calcForm.total_working_days
      });
      setCalculatedSalary(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate salary');
    }
  };

  const handleAttendanceUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    try {
      const response = await api.uploadAttendance(file);
      toast.success(response.data.message || 'Attendance uploaded successfully');
      setShowUploadModal(false);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload attendance');
    } finally {
      setUploading(false);
    }
  };

  const getSalaryTypeLabel = (type) => {
    const labels = {
      'hourly_teachers': 'Hourly (Teachers)',
      'daily_attendance_teachers': 'Daily Attendance (Teachers)',
      'daily_attendance_non_teaching': 'Daily Attendance (Non-Teaching)'
    };
    return labels[type] || type;
  };

  const getSalaryTypeBadgeColor = (type) => {
    const colors = {
      'hourly_teachers': 'bg-blue-100 text-blue-700',
      'daily_attendance_teachers': 'bg-amber-100 text-amber-700',
      'daily_attendance_non_teaching': 'bg-green-100 text-green-700'
    };
    return colors[type] || 'bg-slate-100 text-slate-700';
  };

  const filteredEmployees = employees.filter(emp =>
    emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.profile?.employee_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.profile?.department?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group by department
  const groupedByDept = {};
  filteredEmployees.forEach(emp => {
    const dept = emp.profile?.department || 'Unassigned';
    if (!groupedByDept[dept]) groupedByDept[dept] = [];
    groupedByDept[dept].push(emp);
  });

  const toggleDept = (dept) => {
    setExpandedDepts(prev => ({ ...prev, [dept]: !prev[dept] }));
  };

  if (!pinVerified) {
    return <PINGate module="hr" onVerified={() => setPinVerified(true)} />;
  }

  // Render fields based on salary type
  const renderSalaryTypeFields = () => {
    switch (salaryConfig.salary_type) {
      case 'hourly_teachers':
        return (
          <>
            <div className="space-y-2">
              <Label>Fix Hourly Rate (₹) *</Label>
              <Input
                type="number"
                value={salaryConfig.fix_hourly_rate}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, fix_hourly_rate: parseFloat(e.target.value) || 0 })}
                placeholder="Enter hourly rate"
              />
            </div>
            <div className="space-y-2">
              <Label>Bonus (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.bonus}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, bonus: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Penalty (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.penalty}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, penalty: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Increment (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.increment}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, increment: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>TDS (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.tds}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, tds: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Arrear (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.arrear}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, arrear: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>GWR (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.gwr}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, gwr: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </>
        );
      
      case 'daily_attendance_teachers':
        return (
          <>
            <div className="space-y-2">
              <Label>Fix Gross (₹) *</Label>
              <Input
                type="number"
                value={salaryConfig.fix_gross}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, fix_gross: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Minimum Fix Hours Commitment</Label>
              <Input
                type="number"
                value={salaryConfig.min_fix_hours_commitment}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, min_fix_hours_commitment: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Fix PF (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.fix_pf}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, fix_pf: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Fix ESIC (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.fix_esic}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, fix_esic: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Arrear (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.arrear}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, arrear: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>TDS (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.tds}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, tds: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Bonus (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.bonus}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, bonus: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Penalty (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.penalty}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, penalty: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Increment (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.increment}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, increment: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>GWR (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.gwr}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, gwr: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </>
        );
      
      case 'daily_attendance_non_teaching':
        return (
          <>
            <div className="space-y-2">
              <Label>Fix Gross (₹) *</Label>
              <Input
                type="number"
                value={salaryConfig.fix_gross}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, fix_gross: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Fix PF (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.fix_pf}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, fix_pf: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Fix ESIC (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.fix_esic}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, fix_esic: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Arrear (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.arrear}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, arrear: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>TDS (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.tds}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, tds: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Bonus (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.bonus}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, bonus: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Penalty (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.penalty}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, penalty: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Consistency Bonus (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.consistency_bonus}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, consistency_bonus: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>GWR (₹)</Label>
              <Input
                type="number"
                value={salaryConfig.gwr}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, gwr: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Salary Management</h1>
          <p className="text-slate-500">Configure and calculate employee salaries</p>
        </div>
        <Button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2"
        >
          <Upload size={18} />
          Upload Attendance
        </Button>
      </div>

      {/* Search and View Toggle */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="relative w-full md:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button 
            variant={viewMode === 'department' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setViewMode('department')}
          >
            <Building2 className="h-4 w-4 mr-1" /> By Department
          </Button>
          <Button 
            variant={viewMode === 'list' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <Users className="h-4 w-4 mr-1" /> List View
          </Button>
        </div>
      </div>

      {/* Employees List - Department Wise */}
      {viewMode === 'department' ? (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading employees...</div>
          ) : Object.keys(groupedByDept).length === 0 ? (
            <div className="text-center py-8 text-slate-500">No employees found</div>
          ) : (
            Object.keys(groupedByDept).sort().map(dept => (
              <Card key={dept}>
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                  onClick={() => toggleDept(dept)}
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-indigo-500" />
                    <span className="font-semibold text-slate-900">{dept}</span>
                    <Badge variant="outline">{groupedByDept[dept].length}</Badge>
                  </div>
                  {expandedDepts[dept] ? (
                    <ChevronDown className="h-5 w-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-slate-400" />
                  )}
                </div>
                {expandedDepts[dept] && (
                  <CardContent className="pt-0 space-y-3">
                    {groupedByDept[dept].map(employee => (
                      <div key={employee.id} className="p-3 border rounded-lg hover:shadow-sm">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                              <User className="text-indigo-600" size={20} />
                            </div>
                            <div>
                              <h3 className="font-semibold text-slate-900">
                                {employee.profile?.employee_name || employee.name}
                              </h3>
                              <div className="flex flex-wrap gap-2 mt-1">
                                <span className="text-sm text-slate-500">{employee.email}</span>
                                {employee.profile?.employee_code && (
                                  <Badge variant="outline" className="text-xs">{employee.profile.employee_code}</Badge>
                                )}
                              </div>
                              {employee.salary_config && (
                                <Badge className={`mt-1 ${getSalaryTypeBadgeColor(employee.salary_config.salary_type)}`}>
                                  {getSalaryTypeLabel(employee.salary_config.salary_type)}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openConfigModal(employee)}
                              className="flex items-center gap-1"
                            >
                              <IndianRupee size={14} />
                              Configure
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => openCalcModal(employee)}
                              disabled={!employee.salary_config}
                              className="flex items-center gap-1"
                            >
                              <Calculator size={14} />
                              Calculate
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                )}
              </Card>
            ))
          )}
        </div>
      ) : (
        /* List View */
        <div className="grid gap-4">
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading employees...</div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No employees found</div>
          ) : (
            filteredEmployees.map(employee => (
              <Card key={employee.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                        <User className="text-indigo-600" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">
                          {employee.profile?.employee_name || employee.name}
                        </h3>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="text-sm text-slate-500">{employee.email}</span>
                          {employee.profile?.employee_code && (
                            <Badge variant="outline">{employee.profile.employee_code}</Badge>
                          )}
                          {employee.profile?.department && (
                            <Badge variant="secondary">{employee.profile.department}</Badge>
                          )}
                        </div>
                        {employee.salary_config && (
                          <div className="mt-2">
                            <Badge className={getSalaryTypeBadgeColor(employee.salary_config.salary_type)}>
                              {getSalaryTypeLabel(employee.salary_config.salary_type)}
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openConfigModal(employee)}
                        className="flex items-center gap-1"
                      >
                        <IndianRupee size={14} />
                        Configure
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openCalcModal(employee)}
                        disabled={!employee.salary_config}
                        className="flex items-center gap-1"
                      >
                        <Calculator size={14} />
                        Calculate
                      </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      )}

      {/* Salary Config Modal */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Salary Configuration - {selectedEmployee?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Salary Type */}
            <div className="space-y-2">
              <Label>Salary Type *</Label>
              <Select
                value={salaryConfig.salary_type}
                onValueChange={(value) => setSalaryConfig({ ...salaryConfig, salary_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select salary type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly_teachers">Hourly (Teachers)</SelectItem>
                  <SelectItem value="daily_attendance_teachers">Daily Attendance (Teachers)</SelectItem>
                  <SelectItem value="daily_attendance_non_teaching">Daily Attendance (Non-Teaching)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Dynamic Fields based on salary type */}
            <div className="grid grid-cols-2 gap-4">
              {renderSalaryTypeFields()}
            </div>

            {/* Remarks - common for all */}
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Input
                value={salaryConfig.remarks}
                onChange={(e) => setSalaryConfig({ ...salaryConfig, remarks: e.target.value })}
                placeholder="Any additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>Cancel</Button>
            <Button onClick={saveSalaryConfig} disabled={saving}>
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Calculate Salary Modal */}
      <Dialog open={showCalcModal} onOpenChange={setShowCalcModal}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Calculate Salary - {selectedEmployee?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Month</Label>
                <Select
                  value={String(calcForm.month)}
                  onValueChange={(value) => setCalcForm({ ...calcForm, month: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(12)].map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>
                        {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Year</Label>
                <Input
                  type="number"
                  value={calcForm.year}
                  onChange={(e) => setCalcForm({ ...calcForm, year: parseInt(e.target.value) })}
                />
              </div>
            </div>

            {selectedEmployee?.salary_config?.salary_type === 'hourly_teachers' ? (
              <div className="space-y-2">
                <Label>Month Working Hours</Label>
                <Input
                  type="number"
                  value={calcForm.month_working_hours}
                  onChange={(e) => setCalcForm({ ...calcForm, month_working_hours: parseFloat(e.target.value) || 0 })}
                  placeholder="Leave empty to auto-fetch from Classes"
                />
                <p className="text-xs text-slate-500">Leave empty to auto-calculate from Classes module</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Total Fix Days in {new Date(2000, calcForm.month - 1).toLocaleString('default', { month: 'long' })}:</strong>{' '}
                    {new Date(calcForm.year, calcForm.month, 0).getDate()} days
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Monthly Attendance (Days)</Label>
                    <Input
                      type="number"
                      value={calcForm.monthly_attendance}
                      onChange={(e) => setCalcForm({ ...calcForm, monthly_attendance: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Total Working Days</Label>
                    <Input
                      type="number"
                      value={calcForm.total_working_days}
                      onChange={(e) => setCalcForm({ ...calcForm, total_working_days: parseInt(e.target.value) || 26 })}
                    />
                    <p className="text-xs text-slate-500">Excludes Sundays/holidays</p>
                  </div>
                </div>
              </div>
            )}

            <Button onClick={calculateSalary} className="w-full">
              <Calculator className="mr-2" size={18} />
              Calculate Salary
            </Button>

            {calculatedSalary && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg space-y-3">
                <h4 className="font-semibold text-lg text-slate-900">Salary Breakdown</h4>
                
                {/* Employee Details Section */}
                <div className="p-3 bg-white rounded-lg border space-y-2">
                  <h5 className="font-medium text-slate-700 border-b pb-1">Employee Details</h5>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-slate-500">Name:</span>
                    <span className="font-medium">{calculatedSalary.employee_name}</span>
                    
                    {calculatedSalary.employee_code && (
                      <>
                        <span className="text-slate-500">Employee ID:</span>
                        <span className="font-medium">{calculatedSalary.employee_code}</span>
                      </>
                    )}
                    
                    {calculatedSalary.department && (
                      <>
                        <span className="text-slate-500">Department:</span>
                        <span className="font-medium">{calculatedSalary.department}</span>
                      </>
                    )}
                    
                    {calculatedSalary.designation && (
                      <>
                        <span className="text-slate-500">Designation:</span>
                        <span className="font-medium">{calculatedSalary.designation}</span>
                      </>
                    )}
                    
                    {calculatedSalary.phone_number && (
                      <>
                        <span className="text-slate-500">Phone:</span>
                        <span className="font-medium">{calculatedSalary.phone_number}</span>
                      </>
                    )}
                    
                    {calculatedSalary.personal_email && (
                      <>
                        <span className="text-slate-500">Email:</span>
                        <span className="font-medium">{calculatedSalary.personal_email}</span>
                      </>
                    )}
                    
                    {calculatedSalary.joining_date && (
                      <>
                        <span className="text-slate-500">Joining Date:</span>
                        <span className="font-medium">{calculatedSalary.joining_date}</span>
                      </>
                    )}
                    
                    {calculatedSalary.bank_account_number && (
                      <>
                        <span className="text-slate-500">Bank A/C:</span>
                        <span className="font-medium">{calculatedSalary.bank_account_number} ({calculatedSalary.bank_name || 'N/A'})</span>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Period & Days Section */}
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-slate-500">Period:</span>
                  <span className="font-medium">
                    {new Date(2000, calculatedSalary.month - 1).toLocaleString('default', { month: 'long' })} {calculatedSalary.year}
                  </span>
                  
                  <span className="text-slate-500">Type:</span>
                  <span className="font-medium">{getSalaryTypeLabel(calculatedSalary.salary_type)}</span>
                  
                  {calculatedSalary.total_fix_days && (
                    <>
                      <span className="text-slate-500">Total Fix Days:</span>
                      <span className="font-medium text-blue-600">{calculatedSalary.total_fix_days} days</span>
                    </>
                  )}
                  
                  {calculatedSalary.monthly_attendance !== undefined && (
                    <>
                      <span className="text-slate-500">Attendance:</span>
                      <span className="font-medium">{calculatedSalary.monthly_attendance} / {calculatedSalary.total_working_days} days</span>
                    </>
                  )}
                  
                  {calculatedSalary.month_working_hours !== undefined && calculatedSalary.salary_type === 'hourly_teachers' && (
                    <>
                      <span className="text-slate-500">Working Hours:</span>
                      <span className="font-medium">{calculatedSalary.month_working_hours} hrs</span>
                    </>
                  )}
                </div>

                <hr className="my-2" />

                {/* PF & ESIC Section - For Daily Attendance Types */}
                {(calculatedSalary.fix_pf !== undefined || calculatedSalary.fix_esic !== undefined) && (
                  <>
                    <div className="space-y-1">
                      <h5 className="font-medium text-blue-700">PF & ESIC Calculation</h5>
                      <div className="grid grid-cols-3 gap-2 text-sm bg-blue-50 p-3 rounded-lg">
                        <span className="text-blue-600 font-medium">Component</span>
                        <span className="text-blue-600 font-medium text-center">Fix Amount</span>
                        <span className="text-blue-600 font-medium text-right">Calculated (as per attendance)</span>
                        
                        <span className="text-slate-600">PF</span>
                        <span className="text-center">₹{calculatedSalary.fix_pf?.toLocaleString() || 0}</span>
                        <span className="font-medium text-right text-blue-700">₹{calculatedSalary.calculated_pf?.toLocaleString() || 0}</span>
                        
                        <span className="text-slate-600">ESIC</span>
                        <span className="text-center">₹{calculatedSalary.fix_esic?.toLocaleString() || 0}</span>
                        <span className="font-medium text-right text-blue-700">₹{calculatedSalary.calculated_esic?.toLocaleString() || 0}</span>
                      </div>
                    </div>
                    <hr className="my-2" />
                  </>
                )}

                {/* Earnings */}
                <div className="space-y-1">
                  <h5 className="font-medium text-green-700">Earnings</h5>
                  <div className="grid grid-cols-2 gap-1 text-sm">
                    <span className="text-slate-500">Gross Salary:</span>
                    <span className="font-medium text-right">₹{calculatedSalary.gross_salary?.toLocaleString()}</span>
                    
                    {calculatedSalary.bonus > 0 && (
                      <>
                        <span className="text-slate-500">Bonus:</span>
                        <span className="font-medium text-right">₹{calculatedSalary.bonus?.toLocaleString()}</span>
                      </>
                    )}
                    {calculatedSalary.increment > 0 && (
                      <>
                        <span className="text-slate-500">Increment:</span>
                        <span className="font-medium text-right">₹{calculatedSalary.increment?.toLocaleString()}</span>
                      </>
                    )}
                    {calculatedSalary.arrear > 0 && (
                      <>
                        <span className="text-slate-500">Arrear:</span>
                        <span className="font-medium text-right">₹{calculatedSalary.arrear?.toLocaleString()}</span>
                      </>
                    )}
                    {calculatedSalary.gwr > 0 && (
                      <>
                        <span className="text-slate-500">GWR:</span>
                        <span className="font-medium text-right">₹{calculatedSalary.gwr?.toLocaleString()}</span>
                      </>
                    )}
                    {calculatedSalary.consistency_bonus > 0 && (
                      <>
                        <span className="text-slate-500">Consistency Bonus:</span>
                        <span className="font-medium text-right">₹{calculatedSalary.consistency_bonus?.toLocaleString()}</span>
                      </>
                    )}
                    {calculatedSalary.incentive_points > 0 && (
                      <>
                        <span className="text-slate-500">Incentive Points ({calculatedSalary.incentive_points}):</span>
                        <span className="font-medium text-right text-amber-600">₹{calculatedSalary.incentive_amount?.toLocaleString()}</span>
                      </>
                    )}
                    {calculatedSalary.incentive_paid_cash > 0 && (
                      <>
                        <span className="text-slate-500 text-xs italic">└ Paid in Cash (by Manager):</span>
                        <span className="font-medium text-right text-emerald-600 text-xs">₹{calculatedSalary.incentive_paid_cash?.toLocaleString()} ✓</span>
                      </>
                    )}
                    
                    <span className="text-green-700 font-semibold">Total Earnings:</span>
                    <span className="font-bold text-right text-green-700">₹{calculatedSalary.total_earnings?.toLocaleString()}</span>
                  </div>
                </div>

                {/* Deductions */}
                <div className="space-y-1">
                  <h5 className="font-medium text-red-700">Deductions</h5>
                  <div className="grid grid-cols-2 gap-1 text-sm">
                    {calculatedSalary.penalty > 0 && (
                      <>
                        <span className="text-slate-500">Penalty:</span>
                        <span className="font-medium text-right">₹{calculatedSalary.penalty?.toLocaleString()}</span>
                      </>
                    )}
                    {calculatedSalary.tds > 0 && (
                      <>
                        <span className="text-slate-500">TDS:</span>
                        <span className="font-medium text-right">₹{calculatedSalary.tds?.toLocaleString()}</span>
                      </>
                    )}
                    {calculatedSalary.calculated_pf > 0 && (
                      <>
                        <span className="text-slate-500">Calculated PF:</span>
                        <span className="font-medium text-right">₹{calculatedSalary.calculated_pf?.toLocaleString()}</span>
                      </>
                    )}
                    {calculatedSalary.calculated_esic > 0 && (
                      <>
                        <span className="text-slate-500">Calculated ESIC:</span>
                        <span className="font-medium text-right">₹{calculatedSalary.calculated_esic?.toLocaleString()}</span>
                      </>
                    )}
                    
                    <span className="text-red-700 font-semibold">Total Deductions:</span>
                    <span className="font-bold text-right text-red-700">₹{calculatedSalary.total_deductions?.toLocaleString()}</span>
                  </div>
                </div>

                <hr className="my-2" />

                {/* Net Pay */}
                <div className="flex justify-between items-center bg-indigo-100 p-3 rounded-lg">
                  <span className="text-lg font-semibold text-indigo-900">Net Pay</span>
                  <span className="text-2xl font-bold text-indigo-900">₹{calculatedSalary.net_pay?.toLocaleString()}</span>
                </div>

                {calculatedSalary.remarks && (
                  <div className="text-sm text-slate-500 mt-2">
                    <span className="font-medium">Remarks:</span> {calculatedSalary.remarks}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Attendance Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload Attendance Sheet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Upload an Excel file (.xlsx) with attendance data. The file should have columns for Employee Name, Total P (Present days), etc.
            </p>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleAttendanceUpload}
              disabled={uploading}
            />
            {uploading && <p className="text-sm text-slate-500">Uploading...</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRSalary;
